import { useAuth0 } from "@auth0/auth0-react";
import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { useEffect } from "react";
import { useAccessToken } from "src/hooks/utils/useAccessToken";
import * as datadog from "src/util/datadog";
import { campaignDataRefetchOriginHeader } from "@specsheet-common/shared-types";
import { coreApiURL } from "../constants/url";
import { getCurrentSocketId } from "src/service/websocket/currentSocketId";

const responseInterceptor = (response: AxiosResponse) => {
  return response.data;
};

const errorInterceptor = (response: AxiosError) => {
  datadog.error(response, {});
  return response;
};

const AxiosDefaults = {
  headers: { "Content-Type": "application/json" },
};

const AxiosInstance = axios.create({
  ...AxiosDefaults,
  baseURL: coreApiURL,
});

const AxiosInstanceWithHeaders = axios.create({
  ...AxiosDefaults,
  baseURL: coreApiURL,
});

AxiosInstance.interceptors.response.use(responseInterceptor, errorInterceptor);

AxiosInstance.interceptors.request.use((config) => {
  const socketId = getCurrentSocketId();
  if (socketId && config.headers) {
    config.headers[campaignDataRefetchOriginHeader] = socketId;
  }
  return config;
});

export const useApi = () => {
  const { isAuthenticated } = useAuth0();
  const { getAccessToken } = useAccessToken();
  useEffect(() => {
    const requestInterceptor = async (
      config: InternalAxiosRequestConfig
    ): Promise<InternalAxiosRequestConfig> => {
      const authorizationToken = isAuthenticated
        ? await getAccessToken()
        : undefined;
      if (authorizationToken && config.headers) {
        config.headers.Authorization = authorizationToken;
      }
      return config;
    };
    const interceptorId =
      AxiosInstance.interceptors.request.use(requestInterceptor);
    return () => {
      AxiosInstance.interceptors.request.eject(interceptorId);
    };
  }, [getAccessToken, isAuthenticated]);

  return AxiosInstance;
};
export const apiWithHeaders = AxiosInstanceWithHeaders;
